import * as apiPostProduct from 'api/erp/PostProduct';
import * as apiPostSearch from 'api/erpRecipe/PostErpRecipeSearchV1';
import { Functor } from 'functions/Functor';
import * as StringList from 'types/Product/Search/StringList';
import * as ErpRecipe from 'types/Recipe/Detail/ErpNew/ErpRecipe';
import { ID } from 'types/Recipe/Detail/ErpNew/ErpRecipe';
import * as IngredientList from 'types/Recipe/Detail/ErpNew/IngredientList';
import * as ProductList from 'types/Recipe/Detail/ErpNew/ProductList';

export type Type = ErpRecipe.Type[];

export function create(): Type {
	return [];
}

export async function postToApi(recipeId: string): Promise<apiPostSearch.ResponseData> {
	const response = await apiPostSearch.callApi({ recipeId: recipeId });
	return response.getOrDefault([]);
}

export function getById(obj: Type, id: string | null): ErpRecipe.Type | undefined {
	return obj.find((e) => e.id === id);
}

export function toggleProductSelection(
	obj: Type,
	ingredientId: string,
	productId: string,
	segmentIndex: number
): Type {
	return obj.map((e) =>
		ErpRecipe.toggleProductSelection(e, ingredientId, productId, segmentIndex)
	);
}

function mapFromApiPost(input: apiPostSearch.ResponseData, recipeId: string): Type {
	return input.map((e) => ErpRecipe.mapFromApiSearch(e, recipeId));
}

export async function getErpRecipesByRecipeId(recipeId: string | null): Promise<Type> {
	if (!recipeId) return create();
	return Functor.From(await postToApi(recipeId))
		.map((e) => mapFromApiPost(e, recipeId))
		.get();
}

export function addTemplateRecipe(
	obj: Type,
	recipeId: string,
	ingredients?: IngredientList.Type
): Type {
	if (!ingredients) return obj;
	if (obj.find((e) => e.id === ID.TEMPLATE)) return obj;
	const rawRecipe = ErpRecipe.setMappings(ErpRecipe.create(), ingredients);
	return [{ ...rawRecipe, id: ID.TEMPLATE, recipeId: recipeId }, ...obj];
}

export async function initialize(
	recipeId: string | null,
	ingredients: IngredientList.Type
): Promise<Type> {
	const rawErpRecipes: Type = await Functor.From(recipeId)
		.mapAsync(getErpRecipesByRecipeId)
		.map((e) => addTemplateRecipe(e, recipeId ?? '', ingredients))
		.mapAsync(addOrderGuides)
		.get();

	const productsFromRecipesAndOrderGuides: ProductList.Type = await Functor.From(rawErpRecipes)
		.map(mapToApi)
		.mapAsync(ProductList.getFromApi)
		.get();

	const enrichedErpRecipes: Type = Functor.From(rawErpRecipes)
		.map((e) => replaceProductsInIngredients(e, productsFromRecipesAndOrderGuides))
		.map((e) => addOrderGuideProducts(e, productsFromRecipesAndOrderGuides))
		.get();

	const backupProducts: ProductList.Type = await Functor.From(enrichedErpRecipes)
		.map(getAllIngredientIdsWithEmptyProducts)
		.mapAsync(ProductList.getFromApiSggestions)
		.map(ProductList.mapFromApiSuggestions)
		.get();

	return Functor.From(enrichedErpRecipes)
		.map((e) => mergeWithProducts(e, backupProducts))
		.map(applyIngredientIdToProducts)
		.map(applyOrderGuidePriority)
		.map(sortErpRecipes)
		.map((e) => sortIngredients(e, IngredientList.mapToSortOrder(ingredients)))
		.map(sortProducts)
		.get();
}

function sortErpRecipes(obj: Type): Type {
	return obj.sort((a, b) => {
		if (a.createdDateUtc > b.createdDateUtc) return -1;
		return 1;
	});
}

function sortProducts(obj: Type): Type {
	return obj.map(ErpRecipe.sortProducts);
}

function sortIngredients(obj: Type, sortOrder: { id: string; index: number }[]): Type {
	return obj.map((e) => ErpRecipe.sortIngredients(e, sortOrder));
}

function applyIngredientIdToProducts(obj: Type): Type {
	return obj.map(ErpRecipe.applyIngredientIdToProducts);
}

function applyOrderGuidePriority(obj: Type): Type {
	return obj.map(ErpRecipe.applyOrderGuidePriority);
}

function addOrderGuideProducts(obj: Type, products: ProductList.Type): Type {
	return obj.map((e) => ErpRecipe.addOrderGuideProducts(e, products));
}

function replaceProductsInIngredients(obj: Type, products: ProductList.Type): Type {
	return obj.map((e) => ErpRecipe.replaceProductsInIngredients(e, products));
}

function mapToApi(obj: Type): apiPostProduct.Request[] {
	const data: { productId: string; sourceId: string }[] = [];
	for (const recipe of obj) {
		for (const ingredient of recipe.ingredients) {
			for (const product of ingredient.products) {
				data.push(product);
			}
		}
		for (const directions of Object.values(recipe.orderGuide)) {
			for (const direction of directions) {
				data.push(direction);
			}
		}
	}
	return ProductList.mapToApi(data);
}

async function addOrderGuides(obj: Type): Promise<Type> {
	const output: Type = [];
	for (const i of obj) {
		output.push(await ErpRecipe.addOrderGuide(i));
	}
	return output;
}

function mergeWithProducts(erpRecipes: Type, products: ProductList.Type): Type {
	return erpRecipes.map((e) => {
		return ErpRecipe.mergeWithProducts(e, products);
	});
}

export function mapFirstSuggestionToProduct(obj: Type, erpRecipeId: string | null): Type {
	return obj.map((e) => {
		if (e.id !== erpRecipeId) return e;
		return ErpRecipe.mapFirstSuggestionToProduct(e);
	});
}

export function changeName(obj: Type, id: string, name: string): Type {
	return obj.map((e) => ErpRecipe.changeName(e, id, name));
}

export function getAllIngredientIdsWithEmptyProducts(obj: Type): string[] {
	return Functor.From(obj)
		.map((e) => e.flatMap((e) => e.ingredients))
		.map((e) => e.filter((e) => !e.products.length))
		.map((e) => e.flatMap((e) => e.ingredientId))
		.map(StringList.distinct)
		.get();
}

export function makeNewErpRecipeFromTemplate(obj: Type): Type {
	const newRecipe = obj.find((e) => e.id === ID.NEW);
	if (newRecipe) return obj;
	const templateRecipe = obj.find((e) => e.id === ID.TEMPLATE);
	if (!templateRecipe) return obj;
	return [{ ...templateRecipe, id: ID.NEW }, ...obj];
}

export async function deleteById(obj: Type, erpRecipeId: string): Promise<Type> {
	const output: Type = [];
	for (const i of obj) {
		if (i.id !== erpRecipeId) {
			output.push(i);
		} else {
			if (i.id === ID.NEW) continue;
			await ErpRecipe.deleteFromApi(i.id);
		}
	}
	return output;
}

export function addNewId(obj: Type, oldId: string, newId: string): Type {
	return obj.map((e) => {
		if (e.id !== oldId) return e;
		return { ...e, id: newId };
	});
}

export async function searchAndAddToManuallySearchedProductsToIngredient(
	obj: Type,
	recipeId: string,
	ingredientId: string,
	searchTerm: string,
	nrOfMatches: number
): Promise<Type> {
	const output: Type = [];
	for (const i of obj) {
		if (i.id !== recipeId) {
			output.push(i);
			continue;
		}
		output.push(
			await ErpRecipe.searchAndAddToManuallySearchedProductsToIngredient(
				i,
				ingredientId,
				searchTerm,
				nrOfMatches
			)
		);
	}
	return output;
}

export function resetManuallySearchedProductsFromIngredient(
	obj: Type,
	recipeId: string,
	ingredientId: string
): Type {
	return obj.map((e) => {
		if (e.id !== recipeId) return e;
		return ErpRecipe.resetManuallySearchedProductsFromIngredient(e, ingredientId);
	});
}
